import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { TbCurrencyPound } from 'react-icons/tb';
import { SiStripe } from 'react-icons/si';
import { FiExternalLink } from 'react-icons/fi';

import {
  getTherapistOwnPaymentDetails,
  createTherapistPayment,
  updateTherapistPayment
} from '../../redux/therapistPeymentSlice';
import {
  checkStripeAccountStatus,
  getStripeDashboardLink,
  getStripeOnboardingLink,
  createStripeAccount,
} from '../../redux/stripeSlice';

const PaymentInfo = ({ customStyle = {} }) => {
  const dispatch = useDispatch();
  
  // Redux state - Simplified
  const therapistPaymentsState = useSelector((state) => {
    return state.therapistPayments || {
      ownPaymentDetails: null,
      loading: false,
      error: null,
      successMessage: null
    };
  });
  
  const { ownPaymentDetails, loading, error, successMessage } = therapistPaymentsState;

  const { loading: stripeLoading } = useSelector((state) => state.stripe) || {};

  const [stripeStatus, setStripeStatus] = useState(null); // null | 'active' | 'pending' | 'none'

  // Form states
  const [hourlyPrice, setHourlyPrice] = useState('');
  const [coupleSessionPrice, setCoupleSessionPrice] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [iban, setIban] = useState('');
  const [isPaymentFormValid, setIsPaymentFormValid] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false); // Yeni kullanıcı kontrolü için

  useEffect(() => {
    const fetchStripeStatus = async () => {
      try {
        const result = await dispatch(checkStripeAccountStatus()).unwrap();
        if (result.accountReady) {
          setStripeStatus('active');
        } else if (result.status?.detailsSubmitted) {
          setStripeStatus('pending');
        } else {
          setStripeStatus('none');
        }
      } catch {
        setStripeStatus('none');
      }
    };
    fetchStripeStatus();
  }, [dispatch]);

  const handleStripeManage = async () => {
    try {
      const result = await dispatch(getStripeDashboardLink()).unwrap();
      const url = result.dashboardUrl;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Could not get Stripe dashboard link');
      }
    } catch (err) {
      toast.error(err || 'Failed to open Stripe dashboard');
    }
  };

  const handleStripeSetup = async () => {
    try {
      // Önce hesap oluştur (zaten varsa hata vermez, idempotent)
      await dispatch(createStripeAccount()).unwrap().catch(() => {});
      const result = await dispatch(getStripeOnboardingLink()).unwrap();
      const url = result.onboardingUrl;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Could not get Stripe setup link');
      }
    } catch (err) {
      toast.error(err || 'Failed to start Stripe setup');
    }
  };

  // Component mount olduğunda ödeme detaylarını getir
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const result = await dispatch(getTherapistOwnPaymentDetails()).unwrap();
        if (result) {
          setIsNewUser(false);
        }
      } catch (err) {
        // Eğer 404 hatası alırsak, bu yeni kullanıcı demektir
        if (err.status === 404 || err.message?.includes('not found') || err.message?.includes('bulunamadı')) {
          console.log('Payment details not found for user - this is a new user');
          setIsNewUser(true);
          // Hata state'ini temizle çünkü bu normal bir durum
          // dispatch(clearPaymentError()); // Eğer bu action varsa kullanın
        } else {
          console.error('Error fetching payment details:', err);
        }
      }
    };

    fetchPaymentDetails();
  }, [dispatch]);

  // Ödeme detayları geldiğinde form alanlarını doldur
  useEffect(() => {
    if (ownPaymentDetails) {
      setHourlyPrice(ownPaymentDetails.hourlyPrice?.toString() || '');
      setCoupleSessionPrice(ownPaymentDetails.coupleSessionPrice?.toString() || '');
      setBankName(ownPaymentDetails.bankInfo?.bankName || '');
      setAccountHolder(ownPaymentDetails.bankInfo?.accountHolder || '');
      const rawIban = ownPaymentDetails.bankInfo?.iban || '';
      setIban(formatIban(rawIban));
      setIsNewUser(false);
    }
  }, [ownPaymentDetails]);

  // Success message handling
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
    }
  }, [successMessage]);

  // Form validation - only hourly rate required
  useEffect(() => {
    const isHourlyPriceValid = hourlyPrice && parseFloat(hourlyPrice) > 0;
    setIsPaymentFormValid(isHourlyPriceValid);
  }, [hourlyPrice]);



  // Format IBAN (in groups of 4)
  const formatIban = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    return formatted;
  };



  // Save all changes
  const saveAllChanges = async () => {
    if (!isPaymentFormValid) {
      toast.error('Please provide a valid hourly rate.');
      return;
    }

    try {
      let result;
      
      // Yeni kullanıcı ise veya ownPaymentDetails yoksa create işlemi yap
      if (isNewUser || !ownPaymentDetails || !ownPaymentDetails._id) {
        // Create new record - only hourly rate is required
        const paymentData = {
          hourlyPrice: parseFloat(hourlyPrice)
        };

        if (coupleSessionPrice && parseFloat(coupleSessionPrice) > 0) {
          paymentData.coupleSessionPrice = parseFloat(coupleSessionPrice);
        }

        // Add bank info only if provided
        if (bankName.trim() || accountHolder.trim() || iban.trim()) {
          paymentData.bankInfo = {
            bankName: bankName.trim() || '',
            accountHolder: accountHolder.trim() || '',
            iban: iban.replace(/\s/g, '').toUpperCase() || ''
          };
        }

        result = await dispatch(createTherapistPayment(paymentData));

        if (result.meta.requestStatus === 'fulfilled') {
          toast.success('Payment information created successfully!');
          setIsNewUser(false);
          // Yeni oluşturulan kaydı getir
          dispatch(getTherapistOwnPaymentDetails());
        }

      } else {
        // Update existing record
        const updateData = {
          hourlyPrice: parseFloat(hourlyPrice)
        };

        if (coupleSessionPrice && parseFloat(coupleSessionPrice) > 0) {
          updateData.coupleSessionPrice = parseFloat(coupleSessionPrice);
        } else if (coupleSessionPrice === '') {
          updateData.coupleSessionPrice = null;
        }

        // Add bank info only if provided
        if (bankName.trim() || accountHolder.trim() || iban.trim()) {
          updateData.bankInfo = {
            bankName: bankName.trim() || '',
            accountHolder: accountHolder.trim() || '',
            iban: iban.replace(/\s/g, '').toUpperCase() || ''
          };
        }

        result = await dispatch(updateTherapistPayment({
          id: ownPaymentDetails._id,
          paymentData: updateData
        }));
        
        if (result.meta.requestStatus === 'fulfilled') {
          toast.success('Changes saved successfully!');
          dispatch(getTherapistOwnPaymentDetails());
        }
      }

      // Handle errors
      if (result.meta.requestStatus === 'rejected' && result.payload && result.payload.message) {
        toast.error(result.payload.message);
      }
    } catch (err) {
      console.error('Error saving payment info:', err);
      toast.error('An error occurred while saving');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-gray-600">Loading payment data...</span>
        </div>
      </div>
    );
  }

  // Error state - sadece gerçek hatalar için göster (404 değil)
  if (error && !isNewUser && error.status !== 404) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="text-red-600 text-center py-4">
          <p>Error: {error.message || 'Error loading payment information'}</p>
          <button 
            onClick={() => dispatch(getTherapistOwnPaymentDetails())}
            className="mt-2 bg-button-bg text-white px-4 py-2 rounded-md hover:opacity-90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={customStyle}>
      {/* Current Balance Information - sadece mevcut kullanıcılar için */}
      {ownPaymentDetails && !isNewUser && (
        <div className="bg-gradient-to-r from-background to-white rounded-lg shadow-md p-6 mb-6 text-primary">
          {/* <h2 className="text-xl font-semibold mb-4 flex items-center">
            <TbCurrencyPound className="w-5 h-5 mr-2" />
            Financial Status
          </h2> */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <h3 className="text-sm font-medium mb-1 opacity-90">Total Earnings</h3>
              <p className="text-xl font-bold">
                £{ownPaymentDetails.totalEarnings?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-sm font-medium mb-1 opacity-90">Current Balance</h3>
              <p className="text-xl font-bold">
                £{ownPaymentDetails.balanceAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-sm font-medium mb-1 opacity-90">Individual Rate</h3>
              <p className="text-xl font-bold">
                £{ownPaymentDetails.hourlyPrice?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-sm font-medium mb-1 opacity-90">Couples Rate</h3>
              <p className="text-xl font-bold">
                {ownPaymentDetails.coupleSessionPrice
                  ? `£${ownPaymentDetails.coupleSessionPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                  : <span className="text-base opacity-60">Not set</span>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Yeni kullanıcı bilgilendirme mesajı */}
      {isNewUser && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <TbCurrencyPound className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Welcome! Set up your payment information
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Please enter your hourly rate and bank information to start receiving payments for your therapy sessions.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Account */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2 flex items-center gap-2">
          <SiStripe className="w-5 h-5 text-[#635BFF]" />
          Stripe Account
        </h2>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {stripeStatus === 'active' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Connected
              </span>
            )}
            {stripeStatus === 'pending' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                Under Review
              </span>
            )}
            {stripeStatus === 'none' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                Not Connected
              </span>
            )}
            {stripeStatus === null && (
              <span className="text-sm text-gray-400">Checking status...</span>
            )}

            <p className="text-sm text-gray-500">
              {stripeStatus === 'active' && 'Your Stripe account is active and ready to receive payments.'}
              {stripeStatus === 'pending' && 'Your account details are submitted and under review.'}
              {stripeStatus === 'none' && 'Connect a Stripe account to receive payouts.'}
            </p>
          </div>

          <div>
            {stripeStatus === 'active' && (
              <button
                onClick={handleStripeManage}
                disabled={stripeLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#635BFF] text-white text-sm font-medium hover:bg-[#5147e5] transition-colors disabled:opacity-60"
              >
                <FiExternalLink className="w-4 h-4" />
                {stripeLoading ? 'Opening...' : 'Manage Stripe Account'}
              </button>
            )}
            {(stripeStatus === 'pending' || stripeStatus === 'none') && (
              <button
                onClick={handleStripeSetup}
                disabled={stripeLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#635BFF] text-[#635BFF] text-sm font-medium hover:bg-[#635BFF] hover:text-white transition-colors disabled:opacity-60"
              >
                <SiStripe className="w-4 h-4" />
                {stripeLoading ? 'Loading...' : stripeStatus === 'pending' ? 'Complete Setup' : 'Set Up Stripe'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Fee Settings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2 flex items-center">
          <TbCurrencyPound className="w-5 h-5 mr-2" />
          Fee Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Individual Session Rate (£) *
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full border border-primary rounded-md px-4 py-2 focus:ring-primary focus:border-primary"
              placeholder="e.g. 80.00"
              value={hourlyPrice}
              onChange={(e) => setHourlyPrice(e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              Standard rate per session for individual clients.
            </p>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Couples Session Rate (£)
              <span className="ml-1 text-gray-400 font-normal text-xs">(optional)</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full border border-primary rounded-md px-4 py-2 focus:ring-primary focus:border-primary"
              placeholder="e.g. 140.00"
              value={coupleSessionPrice}
              onChange={(e) => setCoupleSessionPrice(e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              Leave empty to disable couples session booking.
            </p>
          </div>
        </div>
      </div>

      {/* Bank Information - Editable */}
      {/* <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-2 flex items-center">
          <FiCreditCard className="w-5 h-5 mr-2" />
          Bank Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Bank Name
            </label>
          <input
            type="text"
            className="w-full border border-primary rounded-md px-4 py-2 focus:ring-primary focus:border-primary"
            placeholder="Enter bank name (e.g., Garanti BBVA)"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Account Holder Name
            </label>
          <input
            type="text"
            className="w-full border border-primary rounded-md px-4 py-2 focus:ring-primary focus:border-primary"
            placeholder="Enter full name as on bank account"
            value={accountHolder}
            onChange={(e) => setAccountHolder(e.target.value)}
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-medium mb-2">
            IBAN
          </label>
          <input
            type="text"
            maxLength="34"
            className={`w-full border rounded-md px-4 py-2 focus:ring-primary ${
              iban && !validateIban(iban) 
                ? 'border-red-500 focus:border-red-500' 
                : 'border-primary focus:border-primary'
            }`}
            placeholder="TR00 0000 0000 0000 0000 0000 00"
            value={iban}
            onChange={handleIbanChange}
          />
          {iban && !validateIban(iban) && (
            <p className="text-red-500 text-sm mt-1">
              Please enter a valid IBAN number
            </p>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Your earnings will be transferred to this account.
          </p>
        </div>
      </div> */}
      {/* Save Button - Outside card */}
      <div className="flex justify-end">
        <button
          type="button"
          className={`px-6 py-2 rounded-md focus:outline-none flex items-center gap-2 ${
            isPaymentFormValid && !loading
              ? 'bg-gradient-to-l from-white to-cards-border text-primary hover:opacity-90 border border-primary-2'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          onClick={saveAllChanges}
          disabled={!isPaymentFormValid || loading}
        >
          {loading ? 'Saving...' : (isNewUser ? 'Create Payment Info' : 'Save Changes')}
        </button>
      </div>
    </div>
  );
};

export default PaymentInfo;
